import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  RadialBar,
  RadialBarChart,
  PolarAngleAxis,
} from "recharts";
import { motion } from "framer-motion";
import {
  Activity,
  AlertTriangle,
  Users,
  CalendarDays,
  Gauge,
  Download,
  Search,
  LogOut,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";

import { API, api, clearAdminToken, getAdminToken } from "@/lib/api";

const COLORS = [
  "#0052FF",
  "#10B981",
  "#F59E0B",
  "#8B5CF6",
  "#EC4899",
  "#06B6D4",
  "#F97316",
  "#84CC16",
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [list, setList] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [experience, setExperience] = useState("");
  const [monthly, setMonthly] = useState("");

  const token = getAdminToken();
  const logout = () => {
    clearAdminToken();
    navigate("/admin/login");
  };

  useEffect(() => {
    if (!token) {
      navigate("/admin/login");
    }
  }, [token, navigate]);

  const load = async () => {
    setLoading(true);
    try {
      const [d, l] = await Promise.all([
        api.get("/dashboard"),
        api.get("/assessment", {
          params: {
            search: search || undefined,
            experience: experience || undefined,
            monthly_result: monthly || undefined,
          },
        }),
      ]);
      setDashboard(d.data);
      setList(l.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        logout();
      } else {
        toast.error("Failed to load dashboard");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const applyFilters = () => load();

  const downloadFile = async (type) => {
    try {
      const res = await fetch(`${API}/assessment/export/${type}`, {
        headers: { "X-Admin-Token": token || "" },
      });
      if (!res.ok) throw new Error("failed");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `assessments.${type === "csv" ? "csv" : "xlsx"}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  const cards = dashboard?.cards;
  const charts = dashboard?.charts;

  const experienceOptions = useMemo(
    () => (charts?.experience || []).map((c) => c.label).filter((l) => l && l !== "Unknown"),
    [charts]
  );
  const monthlyOptions = useMemo(
    () => (charts?.monthly_result || []).map((c) => c.label).filter((l) => l && l !== "Unknown"),
    [charts]
  );

  if (loading && !dashboard) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8 sm:py-10">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
            Analytics
          </p>
          <h1 className="font-display mt-1 text-3xl sm:text-4xl font-semibold tracking-tight">
            MethodA · Admin
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Real-time snapshot across every assessment submitted.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => downloadFile("csv")}
            data-testid="export-csv-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-border text-sm font-medium hover:bg-accent transition"
          >
            <Download className="w-4 h-4" />
            CSV
          </button>
          <button
            onClick={() => downloadFile("excel")}
            data-testid="export-excel-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-border text-sm font-medium hover:bg-accent transition"
          >
            <Download className="w-4 h-4" />
            Excel
          </button>
          <button
            onClick={logout}
            data-testid="admin-logout-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-border text-sm font-medium hover:bg-accent transition"
          >
            <LogOut className="w-4 h-4" />
            Sign out
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard
          icon={Users}
          label="Total Assessments"
          value={cards?.total_assessments ?? 0}
          testId="card-total"
        />
        <KpiCard
          icon={CalendarDays}
          label="Today"
          value={cards?.today_assessments ?? 0}
          testId="card-today"
        />
        <KpiCard
          icon={Gauge}
          label="Avg Overall Score"
          value={cards?.average_score ?? 0}
          suffix="/100"
          testId="card-avg"
        />
        <KpiCard
          icon={AlertTriangle}
          label="Top Problem"
          value={cards?.most_common_problem ?? "N/A"}
          testId="card-topproblem"
          text
        />
      </div>

      {/* Charts */}
      <div className="mt-6 grid lg:grid-cols-2 gap-4">
        <ChartCard title="Experience Distribution" testId="chart-experience">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={charts?.experience || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
              <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="value" fill="#0052FF" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Trading Style" testId="chart-style">
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie
                data={charts?.trading_style || []}
                dataKey="value"
                nameKey="label"
                innerRadius={50}
                outerRadius={90}
                paddingAngle={2}
              >
                {(charts?.trading_style || []).map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Market Preference" testId="chart-markets">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={charts?.markets || []} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis type="number" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
              <YAxis type="category" dataKey="label" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" width={80} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="value" fill="#10B981" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Monthly Result" testId="chart-monthly">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={charts?.monthly_result || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
              <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" allowDecimals={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="value" fill="#F59E0B" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Psychology Average (1-5)" testId="chart-psychology">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={charts?.psychology_average || []} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis type="number" domain={[0, 5]} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
              <YAxis type="category" dataKey="label" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" width={110} />
              <Tooltip contentStyle={tooltipStyle} />
              <Bar dataKey="value" fill="#8B5CF6" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="City Distribution" testId="chart-city">
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie
                data={(charts?.city || []).slice(0, 8)}
                dataKey="value"
                nameKey="label"
                outerRadius={90}
                paddingAngle={2}
              >
                {(charts?.city || []).slice(0, 8).map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Table + filters */}
      <div className="mt-8 p-4 sm:p-6 rounded-2xl border border-border bg-card soft-shadow">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <h2 className="font-display text-xl font-semibold tracking-tight">
            Recent Assessments
          </h2>
          <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && applyFilters()}
                placeholder="Search name, phone, city…"
                data-testid="admin-search-input"
                className="pl-9 pr-3 py-2 rounded-lg bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition w-full sm:w-64"
              />
            </div>
            <select
              value={experience}
              onChange={(e) => setExperience(e.target.value)}
              data-testid="admin-filter-experience"
              className="px-3 py-2 rounded-lg bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition"
            >
              <option value="">All experience</option>
              {experienceOptions.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
            <select
              value={monthly}
              onChange={(e) => setMonthly(e.target.value)}
              data-testid="admin-filter-monthly"
              className="px-3 py-2 rounded-lg bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition"
            >
              <option value="">All results</option>
              {monthlyOptions.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
            <button
              onClick={applyFilters}
              data-testid="admin-apply-filters-btn"
              className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:-translate-y-[1px] transition"
            >
              Apply
            </button>
          </div>
        </div>

        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm" data-testid="assessments-table">
            <thead className="text-xs uppercase tracking-wider text-muted-foreground">
              <tr className="border-b border-border">
                <Th>Name</Th>
                <Th>Phone</Th>
                <Th>City</Th>
                <Th>Experience</Th>
                <Th>Monthly</Th>
                <Th className="text-right">Overall</Th>
                <Th>Created</Th>
              </tr>
            </thead>
            <tbody>
              {list.items.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-10 text-center text-muted-foreground">
                    No assessments yet.
                  </td>
                </tr>
              )}
              {list.items.map((a) => (
                <tr
                  key={a.id}
                  className="border-b border-border/60 hover:bg-accent/50 transition"
                  data-testid={`row-${a.id}`}
                >
                  <Td className="font-medium">{a.name}</Td>
                  <Td>{a.phone}</Td>
                  <Td>{a.city || "—"}</Td>
                  <Td>{a.experience}</Td>
                  <Td>{a.monthly_result}</Td>
                  <Td className="text-right tabular-nums">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold ${scoreBadge(
                        a.overall_score
                      )}`}
                    >
                      {a.overall_score}
                    </span>
                  </Td>
                  <Td className="text-muted-foreground">
                    {new Date(a.created_at).toLocaleDateString(undefined, {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const tooltipStyle = {
  background: "hsl(var(--card))",
  border: "1px solid hsl(var(--border))",
  borderRadius: 8,
  fontSize: 12,
  boxShadow: "0 8px 30px rgba(0,0,0,0.05)",
};

function KpiCard({ icon: Icon, label, value, suffix, testId, text }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="p-5 rounded-2xl border border-border bg-card soft-shadow hover:border-primary/30 transition"
      data-testid={testId}
    >
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
          {label}
        </p>
        <div className="w-8 h-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
          <Icon className="w-4 h-4 text-primary" />
        </div>
      </div>
      <p
        className={`mt-4 font-display ${text ? "text-xl" : "text-3xl"} font-semibold tracking-tight tabular-nums`}
      >
        {value}
        {suffix && <span className="text-base text-muted-foreground ml-1">{suffix}</span>}
      </p>
    </motion.div>
  );
}

function ChartCard({ title, children, testId }) {
  return (
    <div className="p-5 rounded-2xl border border-border bg-card soft-shadow" data-testid={testId}>
      <div className="flex items-center gap-2 mb-3">
        <Activity className="w-4 h-4 text-primary" />
        <h3 className="text-sm font-semibold tracking-tight">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`text-left font-semibold py-3 px-3 ${className}`}>{children}</th>;
}

function Td({ children, className = "" }) {
  return <td className={`py-3 px-3 ${className}`}>{children}</td>;
}

function scoreBadge(score) {
  if (score >= 80) return "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400";
  if (score >= 65) return "bg-primary/10 text-primary";
  if (score >= 45) return "bg-amber-500/10 text-amber-600 dark:text-amber-400";
  return "bg-red-500/10 text-red-600 dark:text-red-400";
}
